import { Table, TableCell, TableHeader, TableRow } from '@tiptap/extension-table';
import { mergeAttributes } from '@tiptap/core';
import {
	HEADER_BACKGROUND_COLOUR,
	TABLE_CELL_STYLE,
	TABLE_HEADER_STYLE,
	BORDER_COLOUR,
	addBorderAttributes,
	buildTableStyle,
} from './data';
import { selectedRect, TableMap } from 'prosemirror-tables';
import { parseStyleString, styleToString } from '@/utils/emailHelper';

export const CustomTable = Table.extend({
	addAttributes() {
		return {
			...this.parent?.(),

			height: {
				default: null,
				parseHTML: (el) => el.style.height,
			},
			width: {
				default: null,
				parseHTML: (el) => el.style.width,
			},
			style: {
				default: 'overflow:hidden;border-spacing:0;border-collapse:collapse;',
			},
		};
	},

	renderHTML({ HTMLAttributes }) {
		const { height, width, style: _oldStyle } = HTMLAttributes;
		// console.log('renderHTML old style', HTMLAttributes);
		const { height: _h, width: _w, ...styleObj } = parseStyleString(_oldStyle);
		const newStyle = styleToString({ ...styleObj, height, width });

		// console.log('renderHTML new style', newStyle);
		return ['table', mergeAttributes(HTMLAttributes, { style: newStyle }), 0];
	},
	addCommands() {
		return {
			...this.parent?.(),
			toggleHeaderRowBold:
				() =>
				({ state, tr, dispatch }) => {
					if (!dispatch) return false;

					state.doc.descendants((tableNode, tablePos) => {
						if (tableNode.type.name !== 'table') return;

						const rect = selectedRect(state),
							tr = state.tr;

						const boldMark = state.schema.marks.bold;

						const cellsRect = rect.map.cellsInRect({
							left: 0,
							top: rect.top,
							right: rect.map.width,
							bottom: rect.bottom,
						});

						cellsRect.forEach((relativeCellPos) => {
							const cellPos = relativeCellPos + rect.tableStart;
							const cell = tr.doc.nodeAt(cellPos);

							const isHeader = cell.type.name === 'tableHeader';

							cell.descendants((child, offset) => {
								if (child.type.name !== 'paragraph') return;

								const start = cellPos + offset + 1;
								const end = start + child.nodeSize - 2 + 1;

								const mark = boldMark.create();

								if (isHeader) {
									tr.addMark(start, end, mark);
								} else {
									tr.removeMark(start, end, boldMark);
								}
							});
						});

						dispatch(tr);
					});

					return true;
				},

			applyHeaderRowStyles:
				() =>
				({ state, tr }) => {
					state.doc.descendants((tableNode, tablePos) => {
						if (tableNode.type.name !== 'table') return;

						const map = TableMap.get(tableNode);

						const cells = map.cellsInRect({
							left: 0,
							right: map.width,
							top: 0,
							bottom: 1,
						});

						// Determine if the first row is a header row
						const firstRect = map.findCell(cells[0]);
						const firstRow = firstRect.top;
						const firstCol = firstRect.left;

						const firstOffset = map.positionAt(firstRow, firstCol, tableNode);
						const firstPos = tablePos + 1 + firstOffset;

						const firstCell = tr.doc.nodeAt(firstPos);
						const isHeaderRow = firstCell?.type.name === 'tableHeader';

						cells.forEach((cellIndex) => {
							const rect = map.findCell(cellIndex);
							const row = rect.top;
							const col = rect.left;

							const cellOffset = map.positionAt(row, col, tableNode);
							const cellPos = tablePos + 1 + cellOffset;

							const cell = tr.doc.nodeAt(cellPos);
							if (!cell) return;

							tr.setNodeMarkup(cellPos, undefined, {
								...cell.attrs,
								style: isHeaderRow ? TABLE_HEADER_STYLE : TABLE_CELL_STYLE,
								backgroundColor: isHeaderRow ? HEADER_BACKGROUND_COLOUR : null,
							});
						});
					});

					return true;
				},
			setCellBorders:
				({ top, bottom, left, right }) =>
				({ state, tr, dispatch }) => {
					const rect = selectedRect(state);
					const map = TableMap.get(rect.table);

					const cells = map.cellsInRect(rect);

					cells.forEach((cellIndex) => {
						const pos = map.positionAt(
							map.findCell(cellIndex).top,
							map.findCell(cellIndex).left,
							rect.table,
						);

						const cellPos = rect.tableStart + pos;
						const cell = tr.doc.nodeAt(cellPos);

						const attrs = {
							...cell.attrs,
							...(top !== undefined && { borderTop: top }),
							...(bottom !== undefined && { borderBottom: bottom }),
							...(left !== undefined && { borderLeft: left }),
							...(right !== undefined && { borderRight: right }),
						};

						tr.setNodeMarkup(cellPos, undefined, attrs);
					});

					if (dispatch) dispatch(tr);
					return true;
				},
			setOuterBorders:
				() =>
				({ state, tr, dispatch }) => {
					const rect = selectedRect(state);
					const map = TableMap.get(rect.table);

					const cells = map.cellsInRect(rect);

					const { top, bottom, left, right, table, tableStart } = rect;
					cells.forEach((cellIndex) => {
						const cellRect = map.findCell(cellIndex);

						const isTopRow = cellRect.top === top;
						const isBottomRow = cellRect.bottom === bottom;
						const isLeftCol = cellRect.left === left;
						const isRightCol = cellRect.right === right;

						const pos = map.positionAt(cellRect.top, cellRect.left, table);
						const cellPos = tableStart + pos;
						const cell = tr.doc.nodeAt(cellPos);

						const attrs = {
							...cell.attrs,
							...(isTopRow && { borderTop: true }),
							...(isBottomRow && { borderBottom: true }),
							...(isLeftCol && { borderLeft: true }),
							...(isRightCol && { borderRight: true }),
						};

						tr.setNodeMarkup(cellPos, undefined, attrs);
					});

					if (dispatch) dispatch(tr);
					return true;
				},
			setInnerBorders:
				() =>
				({ state, tr, dispatch }) => {
					const rect = selectedRect(state);
					const map = TableMap.get(rect.table);
					const cells = map.cellsInRect(rect);

					const { bottom, right, table, tableStart } = rect;
					cells.forEach((cellIndex) => {
						const cellRect = map.findCell(cellIndex);

						const isLastRow = cellRect.bottom === bottom;
						const isLastCol = cellRect.right === right;

						console.log('set inner borders', {
							isLastRow,
							isLastCol,
							rect,
						});
						const pos = map.positionAt(cellRect.top, cellRect.left, table);
						const cellPos = tableStart + pos;
						const cell = tr.doc.nodeAt(cellPos);

						const attrs = {
							...cell.attrs,
							...(!isLastRow && { borderBottom: true }),
							...(!isLastCol && { borderRight: true }),
						};

						tr.setNodeMarkup(cellPos, undefined, attrs);
					});

					if (dispatch) dispatch(tr);
					return true;
				},
		};
	},
});

export const CustomTableRow = TableRow.extend({
	addAttributes() {
		return {
			...this.parent?.(),
			style: {
				default: null,
			},
		};
	},
	renderHTML({ HTMLAttributes }) {
		return ['tr', mergeAttributes(HTMLAttributes), 0];
	},
});

export const CustomTableHeader = TableHeader.extend({
	addOptions() {
		return {
			...this.parent?.(),
			defaultBorderColor: BORDER_COLOUR,
			defaultBorders: {
				top: false,
				bottom: true,
				left: false,
				right: false,
			},
		};
	},
	addAttributes() {
		return {
			...this.parent?.(),
			...addBorderAttributes({
				defaultBorderColor: this.options.defaultBorderColor,
				defaultBorders: this.options.defaultBorders,
				defaultStyle: TABLE_HEADER_STYLE,
				defaultBackgroundColor: HEADER_BACKGROUND_COLOUR,
			}),
		};
	},

	renderHTML({ HTMLAttributes }) {
		const style = buildTableStyle(HTMLAttributes, TABLE_HEADER_STYLE);
		return ['th', { ...HTMLAttributes, style }, 0];
	},
});

export const CustomTableCell = TableCell.extend({
	addOptions() {
		return {
			...this.parent?.(),
			defaultBorderColor: BORDER_COLOUR,
			defaultBorders: {
				top: false,
				bottom: true,
				left: false,
				right: false,
			},
		};
	},
	addAttributes() {
		return {
			...this.parent?.(),
			...addBorderAttributes({
				defaultBorderColor: this.options.defaultBorderColor,
				defaultBorders: this.options.defaultBorders,
				defaultStyle: TABLE_CELL_STYLE,
				defaultBackgroundColor: null,
			}),
		};
	},

	renderHTML({ HTMLAttributes }) {
		const style = buildTableStyle(HTMLAttributes, TABLE_CELL_STYLE);
		return ['td', { ...HTMLAttributes, style }, 0];
	},
});
